import { useCallback, useEffect, useRef, useState } from 'react';

// Subjects are positional in the blocking model (subject 1, subject 2...) and can
// carry an id/label; prefer that, otherwise their slot number is their identity.
const subjectKey = (p, slot) => p.id ?? p.label ?? `subject-${slot + 1}`;

/**
 * Top-down blocking diagram for one moment, drawn over the REAL site map.
 *
 * Stage grid: x 0 (west) → 100 (east), y 0 (north) → 100 (south); `site.extent_m`
 * metres across. Headings are compass degrees (0 = N, 90 = E).
 *
 * Layers (bottom → top): site features (water, lawns, paved, buildings, paths,
 * steps, roads, trees, landmarks) · grid · sun day-path · shadows · light ray ·
 * lens wedge · movement arrows · subjects · camera · optional LIVE "now" sun.
 *
 * Editing: pass `editable` + `onCommit(blocking)`; subjects, camera and the
 * camera-move target become draggable (pointer events, commit on release).
 */
const SIZE = 260;
const PAD = 26;
const INNER = SIZE - PAD * 2;

const toPx = (v) => PAD + (v / 100) * INNER;
const fromPx = (px) => ((px - PAD) / INNER) * 100;
const rad = (deg) => ((deg - 90) * Math.PI) / 180;
const polar = (cx, cy, r, deg) => [cx + r * Math.cos(rad(deg)), cy + r * Math.sin(rad(deg))];
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const headingBetween = (a, b) => ((Math.atan2(b.x - a.x, a.y - b.y) * 180) / Math.PI + 360) % 360;

function wedgePath(cx, cy, r, heading, fov) {
  const [x1, y1] = polar(cx, cy, r, heading - fov / 2);
  const [x2, y2] = polar(cx, cy, r, heading + fov / 2);
  return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${fov > 180 ? 1 : 0} 1 ${x2} ${y2} Z`;
}

const pathFrom = (points, close = false) =>
  points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${toPx(x).toFixed(1)} ${toPx(y).toFixed(1)}`).join(' ') + (close ? ' Z' : '');

const lightLabel = (offset) => {
  if (offset == null) return null;
  if (offset < 45) return 'Backlit';
  if (offset < 135) return 'Side light';
  return 'Front light';
};

const LAYER_STYLE = {
  water: { fill: '#12283A', stroke: '#2E5C80' },
  green: { fill: '#14231A', stroke: '#24402C' },
  sand: { fill: '#2A2418', stroke: '#3D3524' },
  paved: { fill: '#161618', stroke: '#26262A' },
  building: { fill: '#2A2A2E', stroke: '#4A4A50' },
  pier: { fill: '#2B2620', stroke: '#5A4E3C' },
};

function SiteLayer({ site }) {
  if (!site?.features?.length) return null;
  const polys = site.features.filter((f) => f.kind === 'polygon');
  const lines = site.features.filter((f) => f.kind === 'line');
  const points = site.features.filter((f) => f.kind === 'point');
  const order = ['water', 'green', 'sand', 'paved', 'pier', 'building'];
  polys.sort((a, b) => order.indexOf(a.layer) - order.indexOf(b.layer));
  return (
    <g clipPath="url(#bd-clip)">
      {polys.map((f) => {
        const st = LAYER_STYLE[f.layer] || LAYER_STYLE.paved;
        return <path key={f.id} d={pathFrom(f.points, true)} fill={st.fill} stroke={st.stroke} strokeWidth="0.8" fillOpacity={f.layer === 'building' ? 1 : 0.9} />;
      })}
      {lines.map((f) => {
        const d = pathFrom(f.points);
        if (f.layer === 'water') return <path key={f.id} d={d} fill="none" stroke="#2E5C80" strokeWidth={f.sub === 'stream' ? 3 : 1.6} strokeLinecap="round" />;
        if (f.layer === 'road_major') return <path key={f.id} d={d} fill="none" stroke="#3B3B40" strokeWidth={f.bridge ? 7 : 6} strokeLinecap="round" opacity={f.tunnel ? 0.3 : 1} />;
        if (f.layer === 'road') return <path key={f.id} d={d} fill="none" stroke="#33333A" strokeWidth={4} strokeLinecap="round" opacity={f.tunnel ? 0.3 : 1} />;
        if (f.layer === 'rail') return <path key={f.id} d={d} fill="none" stroke="#3A3A3D" strokeWidth={2.5} strokeDasharray="3 2" />;
        if (f.layer === 'steps') return <path key={f.id} d={d} fill="none" stroke="#8C8378" strokeWidth={3} strokeDasharray="1.2 1.6" strokeLinecap="butt" />;
        if (f.layer === 'path') return <path key={f.id} d={d} fill="none" stroke={f.bridge ? '#8C7A55' : '#59534B'} strokeWidth={f.bridge ? 3 : 1.8} strokeDasharray={f.bridge ? '' : '3 2'} strokeLinecap="round" />;
        if (f.layer === 'pier') return <path key={f.id} d={d} fill="none" stroke="#5A4E3C" strokeWidth={3} strokeLinecap="round" />;
        if (f.layer === 'tree_row') return <path key={f.id} d={d} fill="none" stroke="#2F5A3A" strokeWidth={3.5} strokeDasharray="2 3" strokeLinecap="round" />;
        if (f.layer === 'barrier') return <path key={f.id} d={d} fill="none" stroke="#5C5650" strokeWidth={1} strokeDasharray="1 1.5" />;
        if (f.layer === 'green') return <path key={f.id} d={d} fill="none" stroke="#24402C" strokeWidth={1} />;
        return null;
      })}
      {points.map((f) => {
        const x = toPx(f.x);
        const y = toPx(f.y);
        if (f.layer === 'tree') return <circle key={f.id} cx={x} cy={y} r="3.2" fill="#2F5A3A" stroke="#1B3522" strokeWidth="0.8" />;
        return (
          <g key={f.id}>
            <rect x={x - 2.6} y={y - 2.6} width="5.2" height="5.2" transform={`rotate(45 ${x} ${y})`} fill="#C9A45C" fillOpacity="0.85" />
            {f.label && <text x={x} y={y - 5} fontSize="6.5" fill="#CFC3B3" textAnchor="middle" style={{ paintOrder: 'stroke', stroke: '#0B0B0C', strokeWidth: 2 }}>{f.label}</text>}
          </g>
        );
      })}
      {polys.filter((f) => f.label && f.layer === 'building').slice(0, 4).map((f) => {
        const cx = f.points.reduce((s, p) => s + p[0], 0) / f.points.length;
        const cy = f.points.reduce((s, p) => s + p[1], 0) / f.points.length;
        if (cx < 2 || cx > 98 || cy < 2 || cy > 98) return null;
        return <text key={`l-${f.id}`} x={toPx(cx)} y={toPx(cy)} fontSize="6" fill="#8C8C94" textAnchor="middle">{f.label}</text>;
      })}
    </g>
  );
}

export const MARKER_STYLE = {
  parking: { glyph: 'P', color: '#8FB3E6', label: 'Parking' },
  meeting: { glyph: 'M', color: 'hsl(var(--lxra-gold))', label: 'Meeting point' },
  restroom: { glyph: 'WC', color: '#CFC3B3', label: 'Restroom' },
  tree: { glyph: '♣', color: '#7FBF8A', label: 'Favorite tree' },
  water: { glyph: '≈', color: '#6FA8DC', label: 'Water / fountain' },
  power: { glyph: '⚡', color: '#F2C14E', label: 'Power' },
  hazard: { glyph: '!', color: '#F28B82', label: 'Hazard' },
  other: { glyph: '★', color: '#F5F1E8', label: 'Landmark' },
};

/**
 * @param markers   photographer's pins [{id,x,y,kind,label}] drawn on every diagram
 * @param placing   when set (a marker kind), a click/tap on the stage calls onPlace({x,y})
 * @param onMarkerMove(id,{x,y}) / onMarkerSelect(id)  editing hooks for pins
 */
export const BlockingDiagram = ({
  blocking,
  fovDeg = 40,
  testId,
  sunTrack = [],
  site = null,
  liveSun = null,
  compact = false,
  editable = false,
  onCommit,
  markers = [],
  placing = null,
  onPlace,
  onMarkerMove,
  onMarkerSelect,
  selectedMarkerId = null,
}) => {
  const svgRef = useRef(null);
  const [draft, setDraft] = useState(null); // local blocking while dragging
  const [drag, setDrag] = useState(null); // { kind: 'subject'|'camera'|'move'|'marker', index|id }
  const [markerDraft, setMarkerDraft] = useState(null); // {id, x, y}

  useEffect(() => { setDraft(null); }, [blocking]);
  const pointFromEvent = useCallback((e) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * SIZE;
    const py = ((e.clientY - rect.top) / rect.height) * SIZE;
    return { x: clamp(fromPx(px), 0, 100), y: clamp(fromPx(py), 0, 100) };
  }, []);
  const b = draft || blocking;
  if (!b) return null;

  const cam = b.camera || { x: 50, y: 72, facing: 0 };
  const subjects = b.subjects || [];
  const centroid = subjects.length
    ? { x: subjects.reduce((s, p) => s + p.x, 0) / subjects.length, y: subjects.reduce((s, p) => s + p.y, 0) / subjects.length }
    : { x: 50, y: 45 };
  const camFacing = draft ? headingBetween(cam, centroid) : (cam.facing ?? headingBetween(cam, centroid));
  const cx = toPx(cam.x);
  const cy = toPx(cam.y);
  const center = SIZE / 2;
  const rimR = INNER / 2 + 10;
  const sunAz = b.sun_azimuth;
  const belowHorizon = b.sun_elevation != null && b.sun_elevation < -0.8;
  const [sx, sy] = sunAz != null ? polar(center, center, rimR, sunAz) : [null, null];
  const dist = Math.hypot(cam.x - centroid.x, cam.y - centroid.y);
  const fovR = Math.min(INNER * 0.9, Math.max(40, dist * (INNER / 100) * 1.35));
  const shadowLen = b.sun_elevation != null && b.sun_elevation > 0.5
    ? Math.min(60, Math.max(10, 14 / Math.tan((Math.max(3, b.sun_elevation) * Math.PI) / 180)))
    : 0;
  const scaleUnits = site?.extent_m ? (10 / site.extent_m) * 100 : null; // 10 m in grid units
  const liveBelow = liveSun && liveSun.elevation < -0.8;
  const [lx, ly] = liveSun ? polar(center, center, rimR, liveSun.azimuth) : [null, null];

  // ---- dragging
  const startDrag = (kind, index) => (e) => {
    if (!editable) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.ownerSVGElement?.setPointerCapture?.(e.pointerId);
    setDrag({ kind, index });
    setDraft(JSON.parse(JSON.stringify(blocking)));
  };

  const startMarkerDrag = (id) => (e) => {
    if (!onMarkerMove) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.ownerSVGElement?.setPointerCapture?.(e.pointerId);
    const m = markers.find((x) => x.id === id);
    setDrag({ kind: 'marker', id });
    setMarkerDraft(m ? { id, x: m.x, y: m.y } : null);
  };

  const onStageClick = (e) => {
    if (!placing || !onPlace || drag) return;
    const p = pointFromEvent(e);
    if (p) onPlace({ x: +p.x.toFixed(1), y: +p.y.toFixed(1) });
  };

  const onMove = (e) => {
    if (drag?.kind === 'marker') {
      const p = pointFromEvent(e);
      if (p) setMarkerDraft({ id: drag.id, x: +p.x.toFixed(1), y: +p.y.toFixed(1) });
      return;
    }
    if (!drag || !draft) return;
    const p = pointFromEvent(e);
    if (!p) return;
    const next = { ...draft };
    if (drag.kind === 'subject') {
      next.subjects = draft.subjects.map((s, i) => (i === drag.index ? { ...s, x: +p.x.toFixed(1), y: +p.y.toFixed(1) } : s));
    } else if (drag.kind === 'camera') {
      next.camera = { ...draft.camera, x: +p.x.toFixed(1), y: +p.y.toFixed(1) };
    } else if (drag.kind === 'move') {
      next.camera_move_to = { x: +p.x.toFixed(1), y: +p.y.toFixed(1) };
    }
    setDraft(next);
  };

  const endDrag = () => {
    if (!drag) return;
    if (drag.kind === 'marker') {
      const md = markerDraft;
      setDrag(null);
      setMarkerDraft(null);
      if (md && onMarkerMove) onMarkerMove(md.id, { x: md.x, y: md.y });
      return;
    }
    const committed = draft;
    setDrag(null);
    if (committed && onCommit) onCommit(committed);
  };

  const handleCursor = editable ? 'grab' : 'default';
  const stageCursor = placing ? 'crosshair' : drag ? 'grabbing' : 'default';

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      role="img"
      aria-label="Blocking diagram"
      data-testid={testId}
      className="w-full h-auto select-none"
      onPointerMove={onMove}
      onPointerUp={endDrag}
      onPointerLeave={endDrag}
      onClick={onStageClick}
      style={{ touchAction: editable || placing || onMarkerMove ? 'none' : 'auto', cursor: stageCursor }}
    >
      <defs>
        <clipPath id="bd-clip"><rect x={PAD} y={PAD} width={INNER} height={INNER} rx="10" /></clipPath>
        <marker id="bd-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="hsl(var(--lxra-gold))" />
        </marker>
        <marker id="bd-arrow-muted" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#A9A29A" />
        </marker>
      </defs>

      {/* stage + real site */}
      <rect x={PAD} y={PAD} width={INNER} height={INNER} rx="10" fill="#0B0B0C" stroke="#26262A" />
      <SiteLayer site={site} />
      <g clipPath="url(#bd-clip)">
        {[25, 50, 75].map((g) => (
          <g key={g} stroke="#FFFFFF" strokeOpacity="0.06" strokeDasharray="2 4">
            <line x1={toPx(g)} y1={PAD} x2={toPx(g)} y2={SIZE - PAD} />
            <line x1={PAD} y1={toPx(g)} x2={SIZE - PAD} y2={toPx(g)} />
          </g>
        ))}
      </g>
      <rect x={PAD} y={PAD} width={INNER} height={INNER} rx="10" fill="none" stroke="#26262A" />

      {/* compass (letters step aside when the live sun sits on top of them) */}
      <g fontFamily="inherit" fontSize={compact ? '8' : '9'} fill="#A9A29A" textAnchor="middle">
        {!(liveSun && Math.abs(((liveSun.azimuth - 0 + 540) % 360) - 180) > 168) && <text x={center} y={12}>N</text>}
        {!(liveSun && Math.abs(((liveSun.azimuth - 180 + 540) % 360) - 180) > 168) && <text x={center} y={SIZE - 4}>S</text>}
        {!(liveSun && Math.abs(((liveSun.azimuth - 270 + 540) % 360) - 180) > 168) && <text x={7} y={center + 3}>W</text>}
        {!(liveSun && Math.abs(((liveSun.azimuth - 90 + 540) % 360) - 180) > 168) && <text x={SIZE - 7} y={center + 3}>E</text>}
      </g>

      {/* scale bar */}
      {scaleUnits && (
        <g>
          <line x1={PAD + 6} y1={SIZE - PAD - 6} x2={PAD + 6 + (scaleUnits / 100) * INNER} y2={SIZE - PAD - 6} stroke="#CFC3B3" strokeWidth="1.5" />
          <line x1={PAD + 6} y1={SIZE - PAD - 9} x2={PAD + 6} y2={SIZE - PAD - 3} stroke="#CFC3B3" strokeWidth="1" />
          <line x1={PAD + 6 + (scaleUnits / 100) * INNER} y1={SIZE - PAD - 9} x2={PAD + 6 + (scaleUnits / 100) * INNER} y2={SIZE - PAD - 3} stroke="#CFC3B3" strokeWidth="1" />
          <text x={PAD + 8 + (scaleUnits / 100) * INNER} y={SIZE - PAD - 4} fontSize="7" fill="#CFC3B3">10 m</text>
        </g>
      )}

      {/* day's sun path along the rim */}
      {sunTrack.filter((t) => t.elevation > 0).map((t, i) => {
        const [px, py] = polar(center, center, rimR, t.azimuth);
        return <circle key={t.time ?? t.azimuth} cx={px} cy={py} r="1.6" fill="hsl(var(--lxra-gold))" fillOpacity="0.28" />;
      })}

      {/* shadows */}
      <g clipPath="url(#bd-clip)">
        {shadowLen > 0 && sunAz != null && subjects.map((p, i) => {
          const x = toPx(p.x);
          const y = toPx(p.y);
          const [ex, ey] = polar(x, y, shadowLen, (sunAz + 180) % 360);
          return <line key={`sh-${subjectKey(p, i)}`} x1={x} y1={y} x2={ex} y2={ey} stroke="#000" strokeOpacity="0.55" strokeWidth="6" strokeLinecap="round" />;
        })}
      </g>

      {/* planned sun on the rim + light ray */}
      {sx != null && (
        <g>
          <line x1={sx} y1={sy} x2={center + (sx - center) * 0.25} y2={center + (sy - center) * 0.25} stroke={belowHorizon ? '#4B6B9A' : 'hsl(var(--lxra-gold))'} strokeOpacity="0.5" strokeWidth="1.5" strokeDasharray="3 3" markerEnd="url(#bd-arrow-muted)" />
          <circle cx={sx} cy={sy} r="7" fill={belowHorizon ? '#1E2A3F' : 'hsl(var(--lxra-gold))'} stroke={belowHorizon ? '#4B6B9A' : 'none'} />
          {!belowHorizon && [0, 45, 90, 135, 180, 225, 270, 315].map((a) => {
            const [x1, y1] = polar(sx, sy, 9, a);
            const [x2, y2] = polar(sx, sy, 12, a);
            return <line key={a} x1={x1} y1={y1} x2={x2} y2={y2} stroke="hsl(var(--lxra-gold))" strokeWidth="1.2" />;
          })}
        </g>
      )}

      {/* LIVE sun (right now) */}
      {liveSun && lx != null && (
        <g data-testid="bd-live-sun">
          <circle cx={lx} cy={ly} r="9" fill="none" stroke={liveBelow ? '#6F8FBF' : '#FFF5D6'} strokeWidth="1.2" className="animate-ping" style={{ transformOrigin: `${lx}px ${ly}px`, animationDuration: '2.4s' }} opacity="0.7" />
          <circle cx={lx} cy={ly} r="4.5" fill={liveBelow ? '#233451' : '#FFF5D6'} stroke="#0B0B0C" strokeWidth="1" />
          <text x={lx} y={ly + (ly > center ? 16 : -11)} fontSize="7" fill={liveBelow ? '#8FB3E6' : '#FFF5D6'} textAnchor="middle" fontWeight="600">NOW</text>
        </g>
      )}

      {/* lens wedge */}
      <path d={wedgePath(cx, cy, fovR, camFacing, Math.min(150, fovDeg))} fill="hsl(var(--lxra-gold))" fillOpacity="0.10" stroke="hsl(var(--lxra-gold))" strokeOpacity="0.35" strokeWidth="1" />

      {/* movement arrows */}
      {b.camera_move_to && (
        <g>
          <line x1={cx} y1={cy} x2={toPx(b.camera_move_to.x)} y2={toPx(b.camera_move_to.y)} stroke="hsl(var(--lxra-gold))" strokeWidth="1.5" strokeDasharray="4 3" markerEnd="url(#bd-arrow)" />
          {editable && (
            <circle cx={toPx(b.camera_move_to.x)} cy={toPx(b.camera_move_to.y)} r="7" fill="hsl(var(--lxra-gold))" fillOpacity="0.15" stroke="hsl(var(--lxra-gold))" strokeDasharray="2 2" style={{ cursor: handleCursor }} onPointerDown={startDrag('move')} data-testid="bd-handle-move" />
          )}
        </g>
      )}
      {b.subject_move_to && subjects[0] && (
        <line x1={toPx(subjects[0].x)} y1={toPx(subjects[0].y)} x2={toPx(b.subject_move_to.x)} y2={toPx(b.subject_move_to.y)} stroke="#F5F1E8" strokeOpacity="0.7" strokeWidth="1.5" strokeDasharray="4 3" markerEnd="url(#bd-arrow-muted)" />
      )}

      {/* subjects */}
      {subjects.map((p, i) => {
        const x = toPx(p.x);
        const y = toPx(p.y);
        const facing = draft ? headingBetween(p, cam) : (p.facing ?? 180);
        const [fx, fy] = polar(x, y, 12, facing);
        return (
          <g key={subjectKey(p, i)} style={{ cursor: handleCursor }} onPointerDown={startDrag('subject', i)} data-testid={`bd-handle-subject-${i}`}>
            {editable && <circle cx={x} cy={y} r="11" fill="#F5F1E8" fillOpacity="0.08" />}
            <line x1={x} y1={y} x2={fx} y2={fy} stroke="#F5F1E8" strokeWidth="1.5" />
            <circle cx={x} cy={y} r="6" fill="#F5F1E8" />
            <circle cx={x} cy={y} r="2.5" fill="#0B0B0C" />
            {p.label && <text x={x} y={y - 11} fontSize="8" fill="#CFC3B3" textAnchor="middle" style={{ paintOrder: 'stroke', stroke: '#0B0B0C', strokeWidth: 2 }}>{p.label}</text>}
          </g>
        );
      })}

      {/* photographer's pins */}
      {markers.map((m) => {
        const live = markerDraft && markerDraft.id === m.id ? markerDraft : m;
        const st = MARKER_STYLE[m.kind] || MARKER_STYLE.other;
        const x = toPx(live.x);
        const y = toPx(live.y);
        const sel = selectedMarkerId === m.id;
        return (
          <g
            key={m.id}
            data-testid={`bd-marker-${m.id}`}
            style={{ cursor: onMarkerMove ? 'grab' : onMarkerSelect ? 'pointer' : 'default' }}
            onPointerDown={onMarkerMove ? startMarkerDrag(m.id) : undefined}
            onClick={(e) => { e.stopPropagation(); onMarkerSelect?.(m.id); }}
          >
            {sel && <circle cx={x} cy={y} r="12" fill={st.color} fillOpacity="0.18" />}
            <path d={`M ${x} ${y + 9} L ${x - 6} ${y - 1} A 6 6 0 1 1 ${x + 6} ${y - 1} Z`} fill="#0B0B0C" stroke={st.color} strokeWidth="1.5" />
            <text x={x} y={y + 1} fontSize={m.kind === 'restroom' ? 4.5 : 6.5} fontWeight="700" fill={st.color} textAnchor="middle">{st.glyph}</text>
            {m.label && !compact && <text x={x} y={y + 17} fontSize="7" fill="#CFC3B3" textAnchor="middle">{m.label.slice(0, 18)}</text>}
          </g>
        );
      })}

      {/* camera */}
      <g style={{ cursor: handleCursor }} onPointerDown={startDrag('camera')} data-testid="bd-handle-camera">
        {editable && <circle cx={cx} cy={cy} r="13" fill="hsl(var(--lxra-gold))" fillOpacity="0.10" />}
        <g transform={`translate(${cx} ${cy}) rotate(${camFacing})`}>
          <rect x="-7" y="-5" width="14" height="10" rx="2" fill="hsl(var(--lxra-gold))" />
          <rect x="-3" y="-9" width="6" height="4" rx="1" fill="hsl(var(--lxra-gold))" />
        </g>
        <text x={cx} y={cy + 17} fontSize="8" fill="hsl(var(--lxra-gold))" textAnchor="middle" style={{ paintOrder: 'stroke', stroke: '#0B0B0C', strokeWidth: 2 }}>you</text>
      </g>
    </svg>
  );
};

/** Text legend rendered as HTML beneath the diagram. */
export const BlockingLegend = ({ blocking, fovDeg = 40, site = null }) => {
  if (!blocking) return null;
  const belowHorizon = blocking.sun_elevation != null && blocking.sun_elevation < -0.8;
  const label = lightLabel(blocking.sun_offset_from_axis);
  const unit = site?.unit_m || null;
  const metres = unit && blocking.camera_to_subject_units ? Math.round(blocking.camera_to_subject_units * unit) : null;
  return (
    <div className="mt-1 flex items-center justify-between gap-3 text-[10px] text-[hsl(var(--lxra-muted))]">
      <span>{fovDeg}° lens{metres ? ` · ${metres} m to subject` : blocking.camera_to_subject_units ? ` · ${Math.round(blocking.camera_to_subject_units)} units` : ''}</span>
      {label && (
        <span className={belowHorizon ? 'text-sky-300' : 'text-[hsl(var(--lxra-gold))]'}>
          {belowHorizon ? 'Ambient / blue hour' : `${label} · sun ${Math.round(blocking.sun_offset_from_axis)}° off axis`}
        </span>
      )}
    </div>
  );
};

/** Map key for the site layers, shown once per section. */
export const SiteKey = ({ site }) => {
  if (!site) return null;
  const items = [
    ['building', 'Building', 'bg-[#2A2A2E] border border-[#4A4A50]'],
    ['water', 'Water', 'bg-[#12283A] border border-[#2E5C80]'],
    ['green', 'Lawn / trees', 'bg-[#14231A] border border-[#24402C]'],
    ['path', 'Path', 'border-t-2 border-dashed border-[#59534B] h-0 self-center'],
    ['steps', 'Steps', 'border-t-[3px] border-dotted border-[#8C8378] h-0 self-center'],
    ['road', 'Road', 'border-t-[4px] border-[#33333A] h-0 self-center'],
    ['tree', 'Tree', 'rounded-full bg-[#2F5A3A]'],
    ['poi', 'Landmark', 'rotate-45 bg-[#C9A45C]'],
  ];
  const present = new Set(Object.keys(site.counts || {}));
  const visible = items.filter(([k]) => present.has(k) || (k === 'green' && (present.has('tree_row') || present.has('sand'))) || (k === 'road' && present.has('road_major')));
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[10px] text-[hsl(var(--lxra-muted))]" data-testid="scout-site-key">
      {visible.map(([k, label, cls]) => (
        <span key={k} className="inline-flex items-center gap-1.5">
          <span className={`inline-block h-2.5 w-2.5 ${cls}`} />
          {label}
        </span>
      ))}
      {site.extent_m && <span className="ml-auto">{site.extent_m} m square · {site.attribution || '© OpenStreetMap contributors'}</span>}
    </div>
  );
};
